import { Spinner, StandardTabs } from '@cimplico/components';
import { WorksheetBase, WorksheetStatusEnum } from '../../../../types/worksheet';
import { InternalCommentStatusEnum } from '../../../../types/internal-comment';
import { commentsReducer } from './utils';
import { useState } from 'react';
import { useInternalComments } from '../../../../queries/use-internal-comments';
import { useWorkpaperContext } from '../../../../providers/WorkpaperProvider';
import EmptyStateNew from '../../../../components/empty-state/EmptyStateNew';
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import Comment from './Comment';
import NewComment from './NewComment';

const tabs = [InternalCommentStatusEnum.ACTIVE, InternalCommentStatusEnum.RESOLVED];

type CommentsListProps = {
    worksheet: WorksheetBase;
};

function CommentsList({ worksheet }: CommentsListProps): JSX.Element {
    const { readOnly } = useWorkpaperContext();

    const commentsStats = worksheet.internalComments.reduce(commentsReducer, { active: 0, resolved: 0 });
    const [status, setStatus] = useState(InternalCommentStatusEnum.ACTIVE);
    const [expandNewComment, setExpandNewComment] = useState(false);

    const commentsQuery = useInternalComments(worksheet.workpaperId, {
        filter: `context=${worksheet.id}&status=${status}`,
        search: '',
        sort: 'sort[dir]=asc&sort[field]=updatedAt',
    });

    const comments = commentsQuery.data?.data ?? [];

    return (
        <div className="overflow-auto flex flex-col gap-4 h-full">
            <StandardTabs
                tabs={[{ label: `Active (${commentsStats.active})` }, { label: `Resolved (${commentsStats.resolved})` }]}
                defaultIndex={tabs.indexOf(status)}
                onChange={(i): void => {
                    setStatus(tabs[i]);
                    setExpandNewComment(false);
                }}
            />

            {/* LOADING SPINNER */}
            {commentsQuery.isLoading && (
                <div className="mt-16 mx-auto w-fit">
                    <Spinner size="sm" />
                </div>
            )}

            {/* NEW COMMENT */}
            {status === InternalCommentStatusEnum.ACTIVE &&
                !commentsQuery.isLoading &&
                worksheet.status !== WorksheetStatusEnum.EXCLUDED && (
                    <NewComment
                        worksheetId={worksheet.id}
                        expanded={expandNewComment}
                        onAction={(): void => setExpandNewComment(false)}
                        onExpand={(): void => setExpandNewComment(true)}
                    />
                )}

            {/* NO COMMENTS EMPTY STATE */}
            {comments.length === 0 && !commentsQuery.isLoading && !expandNewComment && (
                <div className="my-4">
                    <EmptyStateNew
                        actionButtonType="secondary"
                        title={`No ${status.toLowerCase()} comments`}
                        icon={<ChatBubbleBottomCenterTextIcon />}
                        titleSize="small"
                        helpText={`${status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()} comments will show here.`}
                        helpLink="https://learn.cimplico.com/docs/worksheet-comments"
                        actionText="New Comment"
                        disableActionButton={worksheet.status === WorksheetStatusEnum.EXCLUDED || readOnly}
                        onClick={status === InternalCommentStatusEnum.ACTIVE ? (): void => setExpandNewComment(true) : undefined}
                    />
                </div>
            )}

            {/* COMMENTS LIST */}
            {comments.length > 0 && (
                <ul className="flex-col-reverse flex gap-4">
                    {comments.map((comment) => {
                        return (
                            <li key={comment.id}>
                                <Comment comment={comment} disableAction={worksheet.status === WorksheetStatusEnum.EXCLUDED || readOnly} />
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

export default CommentsList;
